// API-Client für den Analytics-Bereich (/api/analytics/* Vercel Functions)

import { supabase } from './supabase';
import type { CrmAnfrage } from './crm-api';

export type Channel = 'direct' | 'organic' | 'paid' | 'ai' | 'social' | 'referral';

export interface AnalyticsTimeseriesPoint {
  day: string;
  pageviews: number;
  visitors: number;
}

export interface ChannelStat {
  channel: Channel;
  pageviews: number;
  visitors: number;
  leads: number;
  conversion_rate: number;
}

export interface PageStat {
  site_slug: string;
  path: string;
  pageviews: number;
  visitors: number;
  avg_duration_ms?: number | null;
  avg_scroll_pct?: number | null;
  contact_clicks?: number | null;
}

export interface ProjectStat {
  project_id: string;
  name: string;
  pageviews: number;
  visitors: number;
  leads: number;
  /** Klicks auf Telefonnummer/E-Mail — gleichwertige Kontaktaufnahmen */
  contact_clicks?: number;
  /** LEITZAHL: leads + contact_clicks. conversion_rate rechnet mit dieser Zahl. */
  kontakte?: number;
  conversion_rate: number;
}

export interface CustomerStat {
  customer_id: string;
  name: string;
  pageviews: number;
  visitors: number;
  leads: number;
  /** Klicks auf Telefonnummer/E-Mail — gleichwertige Kontaktaufnahmen */
  contact_clicks?: number;
  /** LEITZAHL: leads + contact_clicks. conversion_rate rechnet mit dieser Zahl. */
  kontakte?: number;
  conversion_rate: number;
}

export interface CampaignStat {
  utm_campaign: string | null;
  campaign_id?: string | null;   // Google-Ads-Kampagnen-ID
  status?: string | null;        // ENABLED | PAUSED (aus Google Ads)
  bidding_strategy?: string | null; // TARGET_SPEND (Klicks max.) | MAXIMIZE_CONVERSIONS
  budget_eur?: number | null;    // Tagesbudget der Kampagne
  /** Von Google GEZÄHLTE Conversions (30 T.) — das Signal, auf das Smart Bidding hört. */
  ads_conversions_30d?: number | null;
  /** Davon Anrufe direkt aus Anzeige/Maps (AD_CALL) — laufen an der Website
   *  und damit am LMS-Tracking vorbei. Typischer PMax-Kanal. */
  ads_anrufe_30d?: number | null;
  /** Klartext, warum eine aktive Kampagne (noch) nicht ausliefert —
   *  z. B. „Startet in Kürze: Anzeigen in Prüfung". null = liefert aus. */
  auslieferung_hinweis?: string | null;
  angle: string | null;          // Angle bzw. Anzeigengruppen-Name (1 AdGroup = 1 Angle)
  ad_group_id?: string | null;
  /** Echter Name der Anzeigengruppe im Ads-Konto (kann von `angle` abweichen). */
  ad_group_name?: string | null;
  is_total?: boolean;            // true = Kampagnen-Summenzeile, false = Anzeigengruppen-Zeile
  channel: Channel | string;
  pageviews: number;
  visitors: number;
  leads: number;
  /** Klicks auf Telefonnummer/E-Mail — gleichwertige Kontaktaufnahmen */
  contact_clicks?: number;
  /** LEITZAHL: leads + contact_clicks. conversion_rate rechnet mit dieser Zahl. */
  kontakte?: number;
  conversion_rate: number;
  cost_eur?: number | null; // Google-Ads-Kosten (Summenzeile: Kampagne, sonst Anzeigengruppe)
  ads_clicks?: number | null;
}

export interface ExcludedCampaign {
  project_id?: string;
  campaign_id: string | null;
  utm_campaign: string | null;
  status: string | null;
  reason: string | null;
  ads_clicks: number | null;
  cost_eur: number | null;
}

export interface AiSourceStat {
  referrer_domain: string;
  pageviews: number;
  visitors: number;
}

export interface AnalyticsStats {
  range: { from: string; to: string };
  timeseries: AnalyticsTimeseriesPoint[];
  totals: { pageviews: number; visitors: number; leads: number; conversion_rate: number; contact_clicks?: number };
  channels: ChannelStat[];
  pages: PageStat[];
  projects: ProjectStat[];
  by_customer?: CustomerStat[];
  campaigns?: CampaignStat[];
  excluded_campaigns?: ExcludedCampaign[];
  ai_sources?: AiSourceStat[];
}

/** Anzeigenamen der KI-Suchmaschinen (Aufschlüsselung des Kanals „KI-Suche"). */
export const aiSourceLabels: Record<string, string> = {
  'chatgpt.com': 'ChatGPT',
  'chat.openai.com': 'ChatGPT',
  'perplexity.ai': 'Perplexity',
  'gemini.google.com': 'Google Gemini',
  'copilot.microsoft.com': 'Microsoft Copilot',
  'claude.ai': 'Claude',
};

export interface SeoTimeseriesPoint {
  date: string;
  clicks: number;
  impressions: number;
  ctr: number | null;
  position: number | null;
}

export interface SeoQuery {
  query: string;
  clicks: number;
  impressions: number;
  position: number;
  /** Vorperiode gleicher Länge — null, wenn der Begriff dort nicht vorkam */
  clicks_prev?: number | null;
  impressions_prev?: number | null;
  position_prev?: number | null;
  /** auf = mehr Impressionen oder bessere Position, ab = umgekehrt, neu = in der Vorperiode nicht da */
  trend?: 'auf' | 'ab' | 'neu' | 'gleich';
}

export interface SeoStats {
  configured: boolean;
  range: { from: string; to: string };
  vorperiode?: { from: string; to: string; clicks: number; impressions: number };
  timeseries: SeoTimeseriesPoint[];
  totals: { clicks: number; impressions: number; ctr: number; position: number };
  top_queries: SeoQuery[];
}

async function analyticsFetch(path: string, customerId?: string): Promise<Response> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Nicht eingeloggt');

  const headers: Record<string, string> = {
    Authorization: `Bearer ${session.access_token}`,
  };
  if (customerId) headers['X-Customer-Id'] = customerId;

  return fetch(path, { method: 'GET', headers });
}

async function parseJson<T>(res: Response): Promise<T> {
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((json as any).error || `Fehler (HTTP ${res.status})`);
  return json as T;
}

export interface StatsParams {
  from: string;
  to: string;
  projectId?: string;
}

export async function getAnalyticsStats(params: StatsParams, customerId?: string): Promise<AnalyticsStats> {
  const q = new URLSearchParams({ from: params.from, to: params.to });
  if (params.projectId) q.set('project_id', params.projectId);
  const res = await analyticsFetch(`/api/analytics/stats?${q.toString()}`, customerId);
  return parseJson<AnalyticsStats>(res);
}

export async function getSeoStats(
  params: { from: string; to: string; projectId: string },
  customerId?: string
): Promise<SeoStats> {
  const q = new URLSearchParams({ from: params.from, to: params.to, project_id: params.projectId });
  const res = await analyticsFetch(`/api/analytics/seo?${q.toString()}`, customerId);
  return parseJson<SeoStats>(res);
}

// ── PUBLIVO-Konnektor (Admin) ───────────────────────────────────────────

export interface PublivoOrg {
  id: string;
  name: string;
  slug: string | null;
  website_url: string | null;
  has_live_blog: boolean;
  blog_domain: string | null;
}

export async function listPublivoOrgs(): Promise<{ configured: boolean; orgs: PublivoOrg[] }> {
  const res = await analyticsFetch('/api/analytics/publivo-orgs');
  return parseJson(res);
}

export async function connectPublivo(projectId: string, publivoOrgId: string | null): Promise<{ success: boolean; connected: boolean }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Nicht eingeloggt');
  const res = await fetch('/api/analytics/connect-publivo', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify({ project_id: projectId, publivo_org_id: publivoOrgId }),
  });
  return parseJson(res);
}

// ── Blog-Analytics ──────────────────────────────────────────────────────

export interface BlogArticlePerf {
  id: string;
  slug: string;
  title: string;
  pillar_name: string | null;
  published_at: string | null;
  status: string;
  view_count: number;
  clicks: number;
  impressions: number;
  position: number | null;
}

export interface BlogStats {
  connected: boolean;
  available?: boolean;
  blog_domain?: string | null;
  blog_status?: string | null; // 'live' | 'pending_dns' | …
  totals?: {
    articles_total: number;
    articles_published: number;
    blog_clicks: number;
    blog_impressions: number;
    main_clicks: number;
    main_impressions: number;
    blog_share_clicks: number;
  };
  timeline?: Array<{ week: string; articles_published: number; blog_clicks: number }>;
  articles?: BlogArticlePerf[];
}

export async function getBlogStats(params: { from: string; to: string; projectId: string }, customerId?: string): Promise<BlogStats> {
  const q = new URLSearchParams({ from: params.from, to: params.to, project_id: params.projectId });
  const res = await analyticsFetch(`/api/analytics/blog?${q.toString()}`, customerId);
  return parseJson<BlogStats>(res);
}

// ── KI-Empfehlungen v2 (Admin, strukturiert) ────────────────────────────
// Das verwendete Modell wird pro Empfehlung in der DB mitgeschrieben und im
// UI aus `model` angezeigt — NIE hartcodieren, sonst zeigt das Panel nach
// einem Modell-Wechsel im Backend dauerhaft den falschen Namen an.

export type RecTarget = 'website' | 'publivo' | 'technik' | 'ki' | 'ads';
export type RecStatus = 'open' | 'applied' | 'dismissed';

export interface SeoRecommendationV2 {
  id: string;
  batch_id: string;
  target: RecTarget;
  rec_type: string;
  title: string;
  reason: string;
  proposal: string;
  prompt: string | null;
  priority: 'hoch' | 'mittel' | 'niedrig' | string;
  page: string | null;
  query: string | null;
  pillar_id: string | null;
  pillar_before: any;
  pillar_after: any;
  apply_payload?: { keyword_text: string; match_type: string; campaign_name: string } | null;
  status: RecStatus;
  applied_at: string | null;
  evaluation: { before?: any; after?: any; verdict?: string } | null;
  evaluated_at: string | null;
  created_at: string;
  /** Modell, das diese Empfehlung erzeugt hat (z. B. "claude-opus-5"). */
  model?: string | null;
}

/**
 * Modell-ID für die Anzeige aufbereiten: "claude-opus-5" → "Opus 5",
 * "claude-opus-4-8" → "Opus 4.8". Datums-Suffixe (z. B. "20251001") werden
 * verworfen. Gibt null zurück, wenn kein Modell bekannt ist.
 */
export function formatModelName(model?: string | null): string | null {
  if (!model) return null;
  const parts = model.replace(/^claude-/, '').split('-').filter(Boolean);
  if (!parts.length) return null;
  const family = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
  const version = parts.slice(1).filter((p) => /^\d{1,2}$/.test(p)).join('.');
  return version ? `${family} ${version}` : family;
}

export interface SeoAlert {
  id: string;
  project_id?: string;
  kind: 'rank_drop' | 'page_down' | 'pagespeed' | 'conversion_upload' | 'website_technik';
  severity: 'warn' | 'critical';
  title: string;
  details: any;
  created_at: string;
}

/** Ein Suchbegriff aus dem Google Keyword Planner mit seinem Verlauf. */
export interface Suchtrend {
  keyword: string;
  /** Ø Suchanfragen je Monat, letzte drei Monate */
  volumen: number;
  /** Ø der drei Monate davor */
  davor: number;
  aenderung_prozent: number;
  /** Vergleich mit denselben Monaten im Vorjahr — null, solange keine 15 Monate Verlauf vorliegen */
  vorjahr_prozent: number | null;
  trend: 'steigt' | 'faellt' | 'stabil' | 'saisonal';
}
export interface Suchtrends {
  /** Letzter Monat mit Daten, YYYY-MM */
  stand: string | null;
  region: string | null;
  gemessen_am: string | null;
  keywords_gesamt: number;
  steigend: Suchtrend[];
  fallend: Suchtrend[];
  top: Array<{ keyword: string; volumen: number }>;
  hinweis?: string | null;
}

export interface RecommendationsResponse {
  /** Aktuell konfiguriertes Modell (womit die nächste Runde rechnet). */
  model?: string | null;
  summary: string | null;
  recommendations: SeoRecommendationV2[];
  alerts: SeoAlert[];
  /** Nachfrage-Trends aus dem Keyword Planner (monatlich gemessen) */
  suchtrends?: Suchtrends | null;
}

async function authedFetch(path: string, init?: RequestInit): Promise<Response> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Nicht eingeloggt');
  return fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      ...(init?.headers || {}),
    },
  });
}

export async function getRecommendations(params: { projectId: string }): Promise<RecommendationsResponse> {
  const res = await analyticsFetch(`/api/analytics/recommendations?project_id=${encodeURIComponent(params.projectId)}`);
  return parseJson(res);
}

export async function generateRecommendations(params: { from: string; to: string; projectId: string }): Promise<RecommendationsResponse> {
  const res = await authedFetch('/api/analytics/recommendations', {
    method: 'POST',
    body: JSON.stringify({ project_id: params.projectId, from: params.from, to: params.to }),
  });
  return parseJson(res);
}

export async function patchRecommendation(
  id: string,
  action: 'applied' | 'dismissed' | 'reopen',
  /** Nur beim Verwerfen: warum. Geht in den Kontext der nächsten Empfehlungsrunde,
   *  damit dieselbe Sache nicht erneut vorgeschlagen wird. */
  grund?: string
): Promise<{ recommendation: SeoRecommendationV2 }> {
  const res = await authedFetch('/api/analytics/recommendations', {
    method: 'PATCH',
    body: JSON.stringify({ id, action, ...(grund ? { grund } : {}) }),
  });
  return parseJson(res);
}

export async function applyPillar(recommendationId: string, confirmNew = false): Promise<{ recommendation: SeoRecommendationV2 }> {
  const res = await authedFetch('/api/analytics/apply-pillar', {
    method: 'POST',
    body: JSON.stringify({ recommendation_id: recommendationId, confirm_new: confirmNew }),
  });
  return parseJson(res);
}

export async function applyAds(recommendationId: string): Promise<{ recommendation: SeoRecommendationV2 }> {
  const res = await authedFetch('/api/analytics/apply-ads', {
    method: 'POST',
    body: JSON.stringify({ recommendation_id: recommendationId }),
  });
  return parseJson(res);
}

/** Kampagne aus der LMS-Auswertung ausschließen (nur Admin, Migration 037). */
export async function excludeCampaign(params: {
  projectId: string;
  campaignId?: string | null;
  campaign?: string | null;
  reason?: string;
}): Promise<{ ok: boolean }> {
  const res = await authedFetch('/api/analytics/exclude-campaign', {
    method: 'POST',
    body: JSON.stringify({
      project_id: params.projectId,
      campaign_id: params.campaignId || undefined,
      campaign: params.campaign || undefined,
      reason: params.reason || undefined,
    }),
  });
  return parseJson(res);
}

/** Kampagnen-Ausschluss wieder aufheben (nur Admin). */
export async function includeCampaign(params: {
  projectId: string;
  campaignId?: string | null;
  campaign?: string | null;
}): Promise<{ ok: boolean }> {
  const res = await authedFetch('/api/analytics/exclude-campaign', {
    method: 'DELETE',
    body: JSON.stringify({
      project_id: params.projectId,
      campaign_id: params.campaignId || undefined,
      campaign: params.campaign || undefined,
    }),
  });
  return parseJson(res);
}

export async function getAlerts(projectId?: string): Promise<{ alerts: SeoAlert[] }> {
  const q = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
  const res = await analyticsFetch(`/api/analytics/alerts${q}`);
  return parseJson(res);
}

export async function resolveAlert(id: string): Promise<{ ok: boolean }> {
  const res = await authedFetch('/api/analytics/alerts', { method: 'PATCH', body: JSON.stringify({ id, action: 'resolve' }) });
  return parseJson(res);
}

// ── Kampagnen-Generator (Admin) ─────────────────────────────────────────

export interface DraftKeyword {
  text: string;
  match_type: string;
  suchvolumen_monat?: number | null; // ECHTES Ø-Monatsvolumen (Keyword Planner)
  wettbewerb?: string | null;        // LOW | MEDIUM | HIGH
  cpc_min_eur?: number | null;       // Gebotsspanne "oben auf der Seite"
  cpc_max_eur?: number | null;
  /** Kein Planner-Treffer — bewusst gesetzter Longtail, kein schlechtes Keyword. */
  longtail?: boolean;
}

/** Rückfrage der KI, wenn das Budget nicht alle Kandidaten trägt — Nico wählt. */
export interface DraftRueckfrage {
  frage: string;
  optionen: Array<{ id: string; label: string; begruendung: string }>;
  empfehlung: string[];
}

/**
 * Phasen-Rechnung je Kampagne (Backend: api/_lib/ads-phase.ts). Sagt, womit die
 * Kampagne startet und wohin sie laufen kann — aus Budget, Klickpreis,
 * Nachfrage und Conversion-Rate, nicht aus einer Kundenklasse.
 */
export interface DraftPhase {
  cpc_eur: number | null;
  cpc_quelle: 'konto' | 'planner' | null;
  conversion_rate: number | null;
  conversion_rate_quelle: 'konto' | 'median' | 'annahme';
  klicks_budget: number | null;
  klicks_nachfrage_min: number | null;
  klicks_nachfrage_max: number | null;
  klicks_pro_monat: number | null;
  begrenzt_durch: 'budget' | 'nachfrage' | null;
  /** true = die Klickzahl stützt sich auf die grobe Nachfrage-Schätzung. */
  nachfrage_geschaetzt: boolean;
  conversions_pro_monat: number | null;
  start_phase: 'daten' | 'conversion' | 'ziel_cpa';
  ziel_phase: 'daten' | 'conversion' | 'ziel_cpa';
  start_strategie: string;
  ziel_strategie: string;
  budget_naechste_stufe_eur: number | null;
  begruendung: string;
}

export interface CampaignDraft {
  id: string;
  draft: {
    angle_check?: string | null;
    budget_einschaetzung?: string | null;
    keyword_recherche?: 'keyword-planner' | 'ohne-volumendaten';
    /** Angrenzende Suchintention, die die KI vorschlägt — auch wenn sie sie nicht gebaut hat. */
    umfeld_vorschlag?: { begriff: string; begruendung: string; cpc_gebot_eur: number | null } | null;
    /** Text für den Kunden — einmal erzeugt und gespeichert, damit er stabil bleibt. */
    briefing?: string | null;
    keyword_planner?: {
      region: string | null;
      cpc_schnitt_eur: number | null;
      ideen_gesamt: number;
      /** Volle Recherche — auch die Begriffe, die es nicht in den Entwurf schafften. */
      ideen?: Array<{
        text: string;
        suchvolumen_monat: number | null;
        wettbewerb: string | null;
        cpc_min_eur: number | null;
        cpc_max_eur: number | null;
      }>;
    } | null;
    seiten_quelle?: 'lms' | 'manifest' | 'angles-meta' | 'nur-startseite';
    rueckfrage?: DraftRueckfrage | null;
    lp_vorschlaege?: string[];
    hinweise?: string[];
    /** Begleit-PMax (Viertel-Regel): wird beim Anlegen automatisch mit erstellt. */
    pmax?: { name: string; tagesbudget_eur: number } | null;
    campaigns: Array<{
      name: string;
      tagesbudget_eur: number;
      phase?: DraftPhase | null;
      negative_keywords?: Array<{ text: string; match_type: string }>;
      ad_groups: Array<{
        name: string;
        final_url: string;
        keywords: DraftKeyword[];
        headlines: string[];
        descriptions: string[];
        /** 'umfeld' = angrenzende Suchintention, eigenes Gebot, eigener Text. */
        typ?: 'kern' | 'umfeld';
        cpc_gebot_eur?: number | null;
      }>;
    }>;
  };
  params?: { budget_eur_pro_tag?: number; geo_modus?: 'umkreis' | 'bundesweit'; ort?: string; umkreis_km?: number; landingpages_auswahl?: string[]; fokus_auswahl?: string[] };
  status: 'entwurf' | 'angelegt' | 'verworfen';
  result?: any;
  created_at: string;
  applied_at?: string | null;
}

export async function getCampaignDraft(projectId: string): Promise<{ draft: CampaignDraft | null }> {
  const res = await analyticsFetch(`/api/analytics/campaign-builder?project_id=${encodeURIComponent(projectId)}`);
  return parseJson(res);
}

export async function generateCampaignDraft(
  projectId: string,
  params: { budget_eur_pro_tag: number; ort: string; umkreis_km: number; geo_modus?: 'umkreis' | 'bundesweit'; landingpages_auswahl?: string[]; fokus_auswahl?: string[] }
): Promise<{ draft: CampaignDraft }> {
  const res = await authedFetch('/api/analytics/campaign-builder', {
    method: 'POST',
    body: JSON.stringify({ project_id: projectId, action: 'entwurf', ...params }),
  });
  return parseJson(res);
}

/**
 * Begleit-PMax für ein Projekt nachrüsten, dessen Suchkampagnen schon im
 * Konto liegen (Viertel-Regel). Idempotent: Hat das Konto bereits eine PMax,
 * antwortet der Server mit `uebersprungen` statt eine zweite anzulegen.
 */
export async function pmaxNachruesten(
  projectId: string,
  tagesbudgetEur: number
): Promise<{ pmax: { kampagne?: string; tagesbudget_eur?: number; uebersprungen?: boolean; grund?: string; error?: string; warnings?: string[] } }> {
  const res = await authedFetch('/api/analytics/campaign-builder', {
    method: 'POST',
    body: JSON.stringify({ project_id: projectId, action: 'pmax_anlegen', tagesbudget_eur: tagesbudgetEur }),
  });
  return parseJson(res);
}

/**
 * Beworbene Landingpages für den Generator. Quelle 'lms' = im Dashboard
 * festgelegt (verbindlich); 'manifest'/'angles-meta' = live von der Website
 * gelesen, weil im LMS noch nichts hinterlegt ist.
 */
export interface CampaignLandingpage {
  url: string;
  dienstleistung: string | null;
  angles: string[];
}

export async function getCampaignLandingpages(
  projectId: string
): Promise<{ quelle: 'lms' | 'manifest' | 'angles-meta' | null; ort_vorschlag?: string | null; seiten: CampaignLandingpage[] }> {
  const res = await analyticsFetch(`/api/analytics/campaign-builder?project_id=${encodeURIComponent(projectId)}&action=seiten`);
  return parseJson(res);
}

/**
 * Keywords einer Anzeigengruppe im Entwurf ersetzen — recherchierte Begriffe
 * nachbuchen oder gebuchte entfernen, bevor die Kampagne im Konto landet.
 */
export async function setDraftKeywords(params: {
  projectId: string;
  draftId: string;
  campaignIndex: number;
  adGroupIndex: number;
  keywords: Array<{ text: string; match_type: string }>;
}): Promise<{ draft: CampaignDraft }> {
  const res = await authedFetch('/api/analytics/campaign-builder', {
    method: 'POST',
    body: JSON.stringify({
      project_id: params.projectId,
      action: 'keywords',
      draft_id: params.draftId,
      campaign_index: params.campaignIndex,
      ad_group_index: params.adGroupIndex,
      keywords: params.keywords,
    }),
  });
  return parseJson(res);
}

/**
 * Briefing für den Kunden zum Entwurf holen. Beim ersten Aufruf erzeugt der
 * Server den Text und speichert ihn; danach kommt derselbe zurück — sonst
 * stünde in der Mail etwas anderes als im System. `neu: true` formuliert neu.
 */
export async function getCampaignBriefing(
  projectId: string,
  draftId: string,
  neu = false
): Promise<{ briefing: string }> {
  const res = await authedFetch('/api/analytics/campaign-builder', {
    method: 'POST',
    body: JSON.stringify({ project_id: projectId, action: 'briefing', draft_id: draftId, neu }),
  });
  return parseJson(res);
}

export async function applyCampaignDraft(projectId: string, draftId: string): Promise<{ draft: CampaignDraft }> {
  const res = await authedFetch('/api/analytics/campaign-builder', {
    method: 'POST',
    body: JSON.stringify({ project_id: projectId, action: 'anlegen', draft_id: draftId }),
  });
  return parseJson(res);
}

// ── Potenzialanalyse (Admin, ohne Kunde/Projekt/Konto) ──────────────────

export interface PotenzialErgebnis {
  frage: string;
  parameter: { ort: string; umkreis_km: number; leistungen: string[]; budget_eur_pro_tag: number; nachbarorte: string[] };
  analyse_md: string;
  keyword_ideen: Array<{
    text: string;
    suchvolumen_monat: number | null;
    wettbewerb: string | null;
    cpc_min_eur: number | null;
    cpc_max_eur: number | null;
  }>;
  region: string | null;
  cpc_schnitt_eur: number | null;
  phase: DraftPhase;
  planner_konto: string | null;
  hinweis: string | null;
  model: string;
}

export async function runPotenzialAnalyse(frage: string): Promise<PotenzialErgebnis> {
  const res = await authedFetch('/api/analytics/potenzial', {
    method: 'POST',
    body: JSON.stringify({ frage }),
  });
  return parseJson(res);
}

/**
 * Potenzialanalyse als gebrandetes PDF holen. Das Ergebnis wird serverseitig
 * nicht gespeichert, deshalb wird es hier mitgeschickt — das PDF zeigt exakt
 * das, was am Bildschirm steht.
 */
async function potenzialPdf(ergebnis: PotenzialErgebnis): Promise<{ url: string; dateiname: string }> {
  const res = await authedFetch('/api/analytics/potenzial-pdf', {
    method: 'POST',
    body: JSON.stringify({
      frage: ergebnis.frage,
      parameter: ergebnis.parameter,
      analyse_md: ergebnis.analyse_md,
      phase: ergebnis.phase,
      cpc_schnitt_eur: ergebnis.cpc_schnitt_eur,
      region: ergebnis.region,
      keyword_ideen: ergebnis.keyword_ideen,
      hinweis: ergebnis.hinweis,
    }),
  });
  return pdfHolen(res, 'Potenzialanalyse.pdf');
}

/** Blob-URL fuer die A4-Vorschau (die Komponente gibt sie selbst wieder frei). */
export async function fetchPotenzialPdfUrl(ergebnis: PotenzialErgebnis): Promise<string> {
  return (await potenzialPdf(ergebnis)).url;
}

export async function openPotenzialPdf(ergebnis: PotenzialErgebnis): Promise<void> {
  const { url, dateiname } = await potenzialPdf(ergebnis);
  pdfSpeichern(url, dateiname);
}

// ── Werbestrategie-Analyse (Admin, Migration 048) ───────────────────────

export interface StrategieAnalyse {
  id: string;
  project_id: string;
  status: 'laeuft' | 'fertig' | 'fehler';
  stufe: 'bestand' | 'recherche' | 'serp' | 'nachfrage' | 'einordnung' | 'plan' | 'auftrag' | 'fertig';
  params: {
    geo_modus?: 'umkreis' | 'bundesweit';
    ort?: string;
    umkreis_km?: number;
    website_url?: string;
    /** Neu strukturiert; ältere Läufe tragen noch den Komma-String. */
    fokus?: string | Array<{ leistung: string; praezisierung?: string }>;
  };
  bestand?: any;
  markt?: any;
  nachfrage?: any;
  plan?: {
    zusammenfassung?: string;
    einordnung?: Array<{
      cluster: string;
      klasse: 'ware' | 'handwerker' | 'situation' | 'orientierung';
      beispiel_begriffe?: string[];
      suchvolumen_monat?: number | null;
      klickpreis_eur?: string | null;
      /** Wie groß der Auftrag typischerweise ist — entscheidet mit, ob beworben wird. */
      auftragswert?: 'gross' | 'mittel' | 'klein';
      auftrag_typisch?: string;
      beleg?: string;
      empfehlung?: string;
    }>;
    /** Was allgemein an der Website fehlt — unabhängig von einzelnen Kampagnen. */
    website_befund?: {
      geprueft?: string;
      luecken?: Array<{ was: string; warum?: string; dringlichkeit?: string }>;
    } | null;
    /**
     * Die eigentliche Entscheidung: 2–3 Kampagnen. Eine Kampagne = eine
     * Leistung = eine Landingpage = 2–3 Angles. Keywords, Anzeigentexte und
     * Gebote entstehen im Google-Ads-Bereich, nicht hier.
     */
    kampagnen?: Array<{
      name: string;
      /**
       * 'grund' = Gewerk + Ort, die Kampagne die immer läuft. Darüber liegt
       * je nach Gewerk 'leistung' (Auftrag wird direkt benannt) oder
       * 'situation' (Lebenssituation) — was passt, entscheidet die Analyse.
       */
      typ?: 'grund' | 'leistung' | 'situation';
      leistung?: string;
      nachfrage?: string;
      /** Was hier typischerweise beauftragt wird — trägt der Auftrag den Klickpreis? */
      auftrag_typisch?: string;
      warum?: string;
      landingpage?: {
        url?: string;
        status?: 'vorhanden' | 'zu_bauen';
        eignung?: 'gut' | 'bedingt';
        begruendung?: string;
        anpassungen?: string[];
      };
      angles?: Array<{ name: string; schluessel?: string; suchintention?: string; url?: string | null }>;
    }>;
    /**
     * Themensäulen für PUBLIVO — in dessen eigenen Feldnamen, damit sie direkt
     * übernommen werden können. Hier landet, was nachweislich gefragt ist, aber
     * kein messbares Suchvolumen hat: Ohne Suchanfragen gibt es keine Auktion,
     * also wird die Nachfrage über Inhalte aufgebaut statt gekauft.
     */
    publivo_themen?: Array<{
      name: string;
      description?: string;
      blog_description?: string;
      research_keywords?: string[];
      example_topics?: string[];
      warum_kein_ads?: string;
      /**
       * Abgleich gegen die real existierenden Säulen der Organisation:
       * neu | nachschaerfen (Name = bestehende Säule) | deckt_sich (nichts zu tun).
       */
      abgleich?: 'neu' | 'nachschaerfen' | 'deckt_sich';
      vorhandene_saeule?: string | null;
      /** Gesetzt, sobald die Säule in PUBLIVO steht — überlebt einen Seitenwechsel. */
      uebernommen?: 'aktualisiert' | 'angelegt';
      pillar_id?: string;
    }>;
    negative_keywords?: string[];
    /** Auftrag für Claude Code: allgemein … */
    website_auftrag?: string | null;
    /** … und je Kampagne einer für genau ihre Landingpage. */
    kampagnen_auftraege?: Array<{ kampagne: string; auftrag: string }>;
    hinweise?: string[];
  } | null;
  fehler?: string | null;
  verbrauch?: Record<string, number>;
  created_at: string;
}

/** Beschriftung der laufenden Stufe — hält die Wartezeit erklärbar. */
export const strategieStufen: Record<string, string> = {
  bestand: 'Website wird gelesen',
  recherche: 'Markt und Wettbewerber',
  serp: 'Suchergebnisse prüfen',
  nachfrage: 'Nachfrage messen',
  einordnung: 'Nachfrage einordnen',
  plan: 'Seiten und Angles festlegen',
  auftrag: 'Auftrag für Claude Code',
  fertig: 'Fertig',
};

export async function getStrategie(projectId: string): Promise<{ analyse: StrategieAnalyse | null }> {
  const res = await analyticsFetch(`/api/analytics/strategie?project_id=${encodeURIComponent(projectId)}`);
  return parseJson(res);
}

export async function startStrategie(
  projectId: string,
  /**
   * `site_passwort` nur nötig, solange die Website hinter der Zugangssperre
   * steht. `fokus` legt fest, für welche Leistungen Kampagnen gebaut werden
   * dürfen — je Eintrag der Begriff (geht in die Google-Saat) und optional die
   * Präzisierung (Zielgruppe/Material, geht nur an die Auswertung). Was der
   * Betrieb wirklich in den Mittelpunkt stellt, weiß nur der Mensch.
   */
  params: {
    ort: string;
    umkreis_km: number;
    geo_modus?: 'umkreis' | 'bundesweit';
    website_url?: string;
    site_passwort?: string;
    fokus?: Array<{ leistung: string; praezisierung?: string }>;
  }
): Promise<{ analyse: StrategieAnalyse }> {
  const res = await authedFetch('/api/analytics/strategie', {
    method: 'POST',
    body: JSON.stringify({ project_id: projectId, action: 'start', ...params }),
  });
  return parseJson(res);
}

/** Laufende Analyse anhalten. Das Ergebnis der fertigen Stufen bleibt erhalten. */
export async function strategieAbbrechen(
  projectId: string,
  analyseId: string,
  stufe: string
): Promise<{ analyse: StrategieAnalyse | null }> {
  const res = await authedFetch('/api/analytics/strategie', {
    method: 'POST',
    body: JSON.stringify({ project_id: projectId, action: 'abbrechen', analyse_id: analyseId, stufe }),
  });
  return parseJson(res);
}

/** Analyse ganz verwerfen. */
export async function strategieVerwerfen(projectId: string, analyseId: string): Promise<{ analyse: null }> {
  const res = await authedFetch('/api/analytics/strategie', {
    method: 'POST',
    body: JSON.stringify({ project_id: projectId, action: 'verwerfen', analyse_id: analyseId }),
  });
  return parseJson(res);
}

/** Rechnet GENAU EINE Stufe. Der Aufrufer ruft so lange, bis status fertig ist. */
export async function strategieWeiter(projectId: string, analyseId: string): Promise<{ analyse: StrategieAnalyse }> {
  const res = await authedFetch('/api/analytics/strategie', {
    method: 'POST',
    body: JSON.stringify({ project_id: projectId, action: 'weiter', analyse_id: analyseId }),
  });
  return parseJson(res);
}

/**
 * Briefing für den Kunden zur Website. Einmal erzeugt und gespeichert — sonst
 * stünde in der Mail etwas anderes als im System. `neu` formuliert neu.
 */
/**
 * Themensäule nach PUBLIVO übergeben.
 *
 * Kommt `braucht_bestaetigung` zurück, gibt es dort noch keine Säule dieses
 * Namens — eine NEUE anzulegen ist Nicos markierte Ausnahme, nicht der
 * Standard. Dann zeigt das Panel die vorhandenen Säulen und fragt nach, bevor
 * es mit `confirmNew` erneut ruft.
 */
export async function strategieSaeuleUebergeben(
  projectId: string,
  analyseId: string,
  name: string,
  confirmNew = false
): Promise<{ ergebnis?: 'aktualisiert' | 'angelegt'; pillar_id?: string; braucht_bestaetigung?: boolean; vorhandene?: string[] }> {
  const res = await authedFetch('/api/analytics/strategie', {
    method: 'POST',
    body: JSON.stringify({ project_id: projectId, action: 'saeule', analyse_id: analyseId, name, confirm_new: confirmNew }),
  });
  return parseJson(res);
}

export async function getStrategieBriefing(
  projectId: string,
  analyseId: string,
  neu = false
): Promise<{ briefing: string }> {
  const res = await authedFetch('/api/analytics/strategie', {
    method: 'POST',
    body: JSON.stringify({ project_id: projectId, action: 'briefing', analyse_id: analyseId, neu }),
  });
  return parseJson(res);
}

// ── Monats-Report (Admin) ───────────────────────────────────────────────

export interface MonthlyReport {
  id: string;
  month: string;
  content_md: string;
  model?: string;
  created_at: string;
  /** Gesetzt, sobald der Bericht fürs Kundenkonto freigegeben ist. */
  released_at?: string | null;
}

export async function listReports(customerId: string): Promise<{
  reports: Array<{ id: string; month: string; created_at: string; released_at?: string | null }>;
  kunde: string | null;
  report_email: string | null;
  /** Erster Berichtsmonat (YYYY-MM) — frühere Monate lässt die API nicht zu. */
  berichte_ab?: string;
}> {
  const res = await analyticsFetch(`/api/analytics/report?customer_id=${encodeURIComponent(customerId)}`);
  return parseJson(res);
}

export interface AlleBerichteZeile {
  id: string;
  month: string;
  created_at: string;
  released_at: string | null;
  customer_id: string;
  kunde: string;
  report_email: string | null;
  /** false = im Berichtsmonat wurde nichts umgesetzt; null = Kunde ohne optimierbare Projekte */
  optimierung_im_monat?: boolean | null;
}

/** Ampel: letzte Empfehlungsrunde je Kunde (nur Kunden mit optimierbaren Projekten). */
export interface OptimierungsStand {
  customer_id: string;
  kunde: string;
  letzte_runde: string | null;
}

/** Sammelansicht für Nico: alle Berichte aller Kunden, neueste zuerst. */
export async function listAllReports(): Promise<{ berichte: AlleBerichteZeile[]; optimierungs_stand?: OptimierungsStand[]; berichte_ab?: string }> {
  const res = await analyticsFetch('/api/analytics/report?alle=1');
  return parseJson(res);
}

/** Bericht löschen (Admin). */
export async function deleteReport(customerId: string, month: string): Promise<void> {
  const res = await authedFetch('/api/analytics/report', {
    method: 'POST',
    body: JSON.stringify({ customer_id: customerId, month, action: 'delete' }),
  });
  await parseJson(res);
}

/**
 * Bericht nach freier Anweisung überarbeiten. Der Text wird neu geschrieben,
 * die Zahlenbasis (snapshot) bleibt unverändert — deshalb kann das PDF danach
 * nicht vom Text abweichen.
 */
export async function reviseReport(customerId: string, month: string, anweisung: string): Promise<{ report: MonthlyReport }> {
  const res = await authedFetch('/api/analytics/report', {
    method: 'POST',
    body: JSON.stringify({ customer_id: customerId, month, action: 'revise', anweisung }),
  });
  return parseJson(res);
}

export async function getReport(customerId: string, month: string): Promise<{ report: MonthlyReport | null }> {
  const res = await analyticsFetch(`/api/analytics/report?customer_id=${encodeURIComponent(customerId)}&month=${encodeURIComponent(month)}`);
  return parseJson(res);
}

export async function generateReport(customerId: string, month: string): Promise<{ report: MonthlyReport }> {
  const res = await authedFetch('/api/analytics/report', { method: 'POST', body: JSON.stringify({ customer_id: customerId, month }) });
  return parseJson(res);
}

/**
 * Bericht fürs Kundenkonto freigeben bzw. Freigabe zurückziehen (Admin).
 *
 * `benachrichtigen` schickt zusätzlich eine kurze Mail an den Kunden (Resend,
 * Adresse = customers.report_email). Nie automatisch — der Aufrufer entscheidet.
 * Das Ergebnis steht in `mail`: Ohne hinterlegte Adresse oder ohne
 * RESEND_API_KEY wird freigegeben und NICHT gemailt, mit Grund.
 */
export async function releaseReport(
  customerId: string,
  month: string,
  release: boolean,
  benachrichtigen = false
): Promise<{ report: MonthlyReport; mail: { gesendet: boolean; an?: string; grund?: string } | null }> {
  const res = await authedFetch('/api/analytics/report', {
    method: 'POST',
    body: JSON.stringify({ customer_id: customerId, month, action: release ? 'release' : 'unrelease', benachrichtigen }),
  });
  return parseJson(res);
}

/**
 * Monatsbericht als PDF holen und im Browser öffnen.
 *
 * Bewusst per fetch + Blob und nicht als <a href>: Der Endpunkt braucht den
 * Bearer-Token, den ein normaler Link nicht mitschickt. Als Kunde ohne
 * customerId aufrufen — der Endpunkt nimmt dann den eigenen Kunden.
 */
/**
 * PDF holen und als Blob-URL zurueckgeben — Grundlage fuer Vorschau UND Download.
 * Der Aufrufer muss die URL selbst per URL.revokeObjectURL freigeben.
 */
/**
 * Dateiname aus dem Content-Disposition-Header lesen.
 *
 * Nötig, weil der Download über eine Blob-URL läuft — dabei sieht der Browser
 * den Header nicht und würde die Datei sonst „download.pdf" nennen. Der Server
 * ist die einzige Stelle, die Kundenname und Monat kennt, also baut er den
 * Namen, und hier wird er nur übernommen.
 */
function dateinameAus(res: Response, rueckfall: string): string {
  const cd = res.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename="?([^";]+)"?/i);
  return m?.[1]?.trim() || rueckfall;
}

/** Blob-URL + vom Server vorgegebener Dateiname. */
async function pdfHolen(res: Response, rueckfall: string): Promise<{ url: string; dateiname: string }> {
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error((json as any)?.error || `PDF konnte nicht erzeugt werden (${res.status})`);
  }
  const dateiname = dateinameAus(res, rueckfall);
  return { url: URL.createObjectURL(await res.blob()), dateiname };
}

/** Datei mit ihrem richtigen Namen herunterladen. */
function pdfSpeichern(url: string, dateiname: string) {
  const a = document.createElement('a');
  a.href = url;
  a.download = dateiname;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Erst freigeben, wenn der Download angestoßen ist — sofortiges revoke
  // bricht ihn in Safari ab.
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

async function reportPdf(month: string, customerId?: string) {
  const q = new URLSearchParams({ month });
  if (customerId) q.set('customer_id', customerId);
  const res = await analyticsFetch(`/api/analytics/report-pdf?${q.toString()}`);
  return pdfHolen(res, `Monatsbericht_${month}.pdf`);
}

export async function fetchReportPdfUrl(month: string, customerId?: string): Promise<string> {
  return (await reportPdf(month, customerId)).url;
}

export async function openReportPdf(month: string, customerId?: string): Promise<void> {
  const { url, dateiname } = await reportPdf(month, customerId);
  pdfSpeichern(url, dateiname);
}

/**
 * Google-Ads-Sync sofort auslösen (nur Admin). Sonst läuft er nur nachts um
 * 03:30 UTC — wer gerade Kampagnen umbaut, sieht seine Änderung sonst erst am
 * nächsten Morgen im Dashboard.
 */
export async function syncAdsJetzt(): Promise<{ ok: boolean }> {
  const res = await authedFetch('/api/analytics/ads-cron', { method: 'POST' });
  return parseJson(res);
}

// ── Beworbene Landingpages je Projekt (Admin, Migration 047) ────────────

export interface Landingpage {
  id: string;
  project_id: string;
  url: string;
  name: string | null;
  angles: string[];
  angle_quelle: 'manifest' | 'meta' | 'ki' | 'manuell' | null;
  analysiert_am: string | null;
}

interface LpAntwort {
  landingpages: Landingpage[];
  projekt_art?: string;
  vorschlag?: string | null;
  hinweis?: string | null;
  gefunden?: number;
  quelle?: string;
}

export async function getLandingpages(projectId: string): Promise<LpAntwort> {
  const res = await analyticsFetch(`/api/analytics/landingpages?project_id=${encodeURIComponent(projectId)}`);
  return parseJson(res);
}

/** Seite festlegen — die Angles bestimmt der Server sofort mit. */
export async function addLandingpage(projectId: string, url: string): Promise<LpAntwort> {
  const res = await authedFetch('/api/analytics/landingpages', {
    method: 'POST',
    body: JSON.stringify({ project_id: projectId, url }),
  });
  return parseJson(res);
}

export async function analyseLandingpage(projectId: string, id: string): Promise<LpAntwort> {
  const res = await authedFetch('/api/analytics/landingpages', {
    method: 'POST',
    body: JSON.stringify({ project_id: projectId, id, action: 'analysieren' }),
  });
  return parseJson(res);
}

export async function setLandingpageAngles(projectId: string, id: string, angles: string[]): Promise<LpAntwort> {
  const res = await authedFetch('/api/analytics/landingpages', {
    method: 'POST',
    body: JSON.stringify({ project_id: projectId, id, angles }),
  });
  return parseJson(res);
}

export async function deleteLandingpage(projectId: string, id: string): Promise<LpAntwort> {
  const res = await authedFetch('/api/analytics/landingpages', {
    method: 'POST',
    body: JSON.stringify({ project_id: projectId, id, action: 'loeschen' }),
  });
  return parseJson(res);
}

// ── Einrichtungsstand (Admin) ───────────────────────────────────────────

export interface StatusPruefung {
  was: string;
  stand: 'ok' | 'offen' | 'entfaellt';
  detail: string;
}

export interface StatusKunde {
  id: string;
  name: string;
  software_only: boolean;
  pruefungen: StatusPruefung[];
  projekte: Array<{ id: string; name: string; slug: string; art: string; pruefungen: StatusPruefung[]; offen: number }>;
  offen: number;
  hinweis: string | null;
}

/** Einrichtungsstand aller Kunden — aus den Daten abgeleitet, nicht aus einer Liste. */
export async function getOnboardingStatus(): Promise<{ kunden: StatusKunde[]; offen_gesamt: number }> {
  const res = await analyticsFetch('/api/analytics/onboarding-status');
  return parseJson(res);
}

// ── Kundensicht: freigegebene Monats-Berichte ───────────────────────────

export interface MyReportListItem {
  id: string;
  month: string;
  released_at: string;
}

export async function listMyReports(): Promise<{ reports: MyReportListItem[]; report_email?: string | null }> {
  const res = await analyticsFetch('/api/analytics/my-reports');
  return parseJson(res);
}

/** Kunde pflegt seine Berichts-Empfänger selbst (mehrere mit Komma, leer = keine Mail). */
export async function saveMyReportEmail(reportEmail: string): Promise<{ report_email: string | null }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Nicht eingeloggt');
  const res = await fetch('/api/analytics/my-reports', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify({ report_email: reportEmail }),
  });
  return parseJson(res);
}

export async function getMyReport(
  month: string
): Promise<{ report: (MonthlyReport & { released_at: string }) | null }> {
  const res = await analyticsFetch(`/api/analytics/my-reports?month=${encodeURIComponent(month)}`);
  return parseJson(res);
}

// Labels & Farben für die Kanäle (deutsch)
export const channelLabels: Record<Channel, string> = {
  direct: 'Direkt',
  organic: 'Google/Suche',
  paid: 'Google Ads',
  ai: 'KI-Suche',
  social: 'Social Media',
  referral: 'Verweise',
};

export const channelColors: Record<Channel, string> = {
  organic: '#3b82f6', // blau
  paid: '#8b5cf6', // violett
  ai: '#10b981', // grün (KI hervorheben)
  social: '#f59e0b', // amber
  referral: '#06b6d4', // cyan
  direct: '#94a3b8', // grau
};

// ── Potenzial-Check: Bericht (Admin) ────────────────────────────────────────

/** PDF des Potenzial-Berichts frisch bauen und öffnen/speichern. */
export async function openPotenzialBerichtPdf(checkId: string): Promise<void> {
  const res = await analyticsFetch(`/api/potenzial/bericht-pdf?id=${encodeURIComponent(checkId)}`);
  const { url, dateiname } = await pdfHolen(res, 'Potenzial-Bericht.pdf');
  pdfSpeichern(url, dateiname);
}

/** Bericht (erneut) an die hinterlegte E-Mail senden. */
export async function sendePotenzialBericht(checkId: string, email?: string): Promise<{ gesendet: boolean; grund?: string }> {
  const res = await authedFetch('/api/potenzial/bericht-pdf', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: checkId, action: 'senden', ...(email ? { email } : {}) }),
  });
  return parseJson(res);
}

export interface PotenzialAdminStatus {
  check: {
    id: string; status: string; email: string | null; name: string | null;
    bericht_status: string | null; bericht_gesendet_at: string | null; bericht_fehler: string | null; bericht_versuche: number;
    followup_opt_in: boolean; followup_stufe: number; followup_naechste_at: string | null; followup_gestoppt_at: string | null; followup_stopp_grund: string | null;
    created_at: string;
  };
  mails: Array<{ art: string; empfaenger: string; betreff: string | null; gesendet_at: string; fehler: string | null }>;
  followup_tage: number[];
}

/** Admin-Status eines Potenzial-Checks (E-Mail, Bericht, Follow-up, Mail-Log). */
export async function getPotenzialAdmin(checkId: string): Promise<PotenzialAdminStatus> {
  const res = await analyticsFetch(`/api/potenzial/admin?id=${encodeURIComponent(checkId)}`);
  return parseJson(res);
}

/** Follow-up-Sequenz je Kontakt an/aus. */
export async function setzePotenzialFollowup(checkId: string, aktiv: boolean): Promise<PotenzialAdminStatus> {
  const res = await authedFetch('/api/potenzial/admin', { method: 'POST', body: JSON.stringify({ id: checkId, action: 'followup', aktiv }) });
  return parseJson(res);
}

// ── Google Ads direkt steuern (Admin, mit Begründungspflicht) ──────────────

export type AdsAktion =
  | { art: 'kampagne_status'; kampagne: string; status: 'PAUSED' | 'ENABLED' }
  | { art: 'anzeigengruppe_status'; anzeigengruppe_id: string; status: 'PAUSED' | 'ENABLED' }
  | { art: 'gebotsstrategie'; kampagne: string; strategie: 'MAXIMIZE_CLICKS' | 'MAXIMIZE_CONVERSIONS'; cpc_limit_eur?: number | null }
  | { art: 'budget'; kampagne: string; betrag_eur: number }
  /** Von Google beanstandete Texte (z. B. Telefonnummer) aus einer laufenden
   *  Kampagne räumen: saubere Anzeige neu anlegen, alte pausieren. */
  | { art: 'anzeigen_bereinigen'; kampagne: string; ersatztext?: string | null };

/**
 * Änderung direkt im Ads-Konto ausführen. Der Grund ist Pflicht: Aus ihm baut
 * der Monatsbericht die Erklärung für den Kunden, und die Änderung zählt als
 * umgesetzte Maßnahme (Ampel, Wirkungsmessung).
 */
export async function steuereAds(payload: {
  project_id: string;
  grund: string;
  aktion: AdsAktion;
}): Promise<{ ok: boolean; titel: string; warnung?: string }> {
  const res = await authedFetch('/api/analytics/ads-steuern', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return parseJson(res);
}

/**
 * „Optimierungspotenzial der Kampagne" — Nicos wöchentliche Runde auf Abruf:
 * Suchbegriffe durchgehen, ausschließen, einbuchen, Unterseiten-Themen
 * erkennen. Ergebnisse landen als Empfehlungen (GO-Buttons im Empfehlungen-Tab).
 */
export async function kampagnenOptimierung(payload: { project_id: string; campaign: string }): Promise<{
  ok: boolean;
  zusammenfassung: string;
  vorschlaege: Array<{ art: string; target: string; titel: string; begruendung: string }>;
  hinweis: string | null;
}> {
  const res = await authedFetch('/api/analytics/kampagnen-optimierung', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return parseJson(res);
}

// ── Redesign 03.09.2026: Startseite „Heute" + Kundenliste mit Ampeln ─────────

export interface HeuteEintrag {
  art: 'ads' | 'bericht' | 'anfrage' | 'kontakt' | 'technik';
  stufe?: 'rot' | 'gelb';
  kunde: string;
  kunde_id: string | null;
  titel?: string;
  text: string;
  zeit?: string;
  link: string;
}
export interface HeuteDaten {
  datum: string;
  braucht_dich: HeuteEintrag[];
  neu: HeuteEintrag[];
  laeuft: {
    kampagnen_aktiv: number;
    kampagnen_eingeschraenkt: number;
    /** davon in der Lernphase — läuft, nur Information */
    kampagnen_lernphase: number;
    uploads_ok: number;
    uploads_fehler: number;
    zielvorhaben_ok: boolean;
    berichte_versandt: number;
  };
  todos: Array<{ id: string; titel: string; deadline: string | null; prioritaet: string | null; kunde: string | null }>;
}
export async function getHeute(): Promise<HeuteDaten> {
  const res = await analyticsFetch('/api/analytics/heute');
  return parseJson(res);
}

export type AmpelStatus = 'gruen' | 'gelb' | 'rot' | 'grau' | 'keine';
export interface KundeStatus {
  id: string;
  name: string;
  slug: string;
  active: boolean;
  software_only: boolean;
  pakete: string[];
  projekte: Array<{ id: string; name: string; project_type: string }>;
  ads: { status: AmpelStatus; text: string };
  anfragen_30d: { formular: number; klicks: number };
  bericht: { status: AmpelStatus; text: string };
  zuletzt: { text: string; zeit: string } | null;
}
export async function getKundenStatus(): Promise<{ kunden: KundeStatus[] }> {
  const res = await analyticsFetch('/api/analytics/kunden-status');
  return parseJson(res);
}

// ── Leadpool (Nico, 06.09.2026) ─────────────────────────────────────────

export type LeadpoolGruppe = 'aufgehoert' | 'wirkungslos' | 'nie' | 'aktiv' | 'unklar';
export type LeadpoolStatus = 'wartet' | 'neu' | 'angerufen' | 'demo' | 'kunde' | 'nein' | 'nicht_anrufen';

export interface LeadpoolLead {
  id: string;
  created_at: string;
  updated_at: string;
  domain: string;
  website_url: string | null;
  firma: string | null;
  gewerk: string | null;
  gewerk_label: string | null;
  ort: string | null;
  plz: string | null;
  telefon: string | null;
  email: string | null;
  instagram_handle: string | null;
  ig_name: string | null;
  ig_engagement_pct: number | null;
  ig_quelle: string | null;
  ig_status: string | null;
  ig_letzter_beitrag_vor_tagen: number | null;
  ig_beitraege_90: number | null;
  ig_interaktionen_schnitt: number | null;
  ig_follower: number | null;
  /** Regelmäßigkeit (07.09.2026): wie viele Beiträge über welchen Zeitraum, welcher Takt, längste Pause. */
  ig_beitraege_gelistet?: number | null;
  ig_zeitraum_tage?: number | null;
  ig_takt_tage?: number | null;
  ig_luecke_max_tage?: number | null;
  facebook_url: string | null;
  gruppe: LeadpoolGruppe | null;
  score: number;
  begruendung: string | null;
  quelle: string | null;
  quelle_detail: string | null;
  check_id: string | null;
  status: LeadpoolStatus;
  notizen: string | null;
  letzter_kontakt_at: string | null;
  geprueft_at: string | null;
  fehler: string | null;
}

// ── Betriebe / Master-CRM (Nico, 06.09.2026) ─────────────────────────────
// Ein Betrieb = ein Datensatz (Domain ist der Schlüssel), darunter CHANCEN je
// Produkt mit eigener Phase. Der Leadpool-Typ oben ist die Kandidaten-Stufe
// davon (Betrieb erweitert LeadpoolLead); die alte Leadpool-Seite und ihr
// Endpunkt /api/potenzial/leadpool wurden am 06.09.2026 entfernt.
// Endpunkte: /api/crm/betriebe und /api/crm/analyse-pdf (Auth: Admin ODER vertrieb).

export type Produkt = 'publivo' | 'website' | 'google_ads' | 'recruiting' | 'beratung';
export type ChancePhase = 'kandidat' | 'angerufen' | 'gespraech' | 'angebot' | 'testphase' | 'kampagne' | 'kunde' | 'verloren' | 'nicht_anrufen';
export type BetriebStatus = LeadpoolStatus;
export type PublivoStatus = 'trial' | 'active' | 'expired';
export type Anrede = 'Herr' | 'Frau';

export interface Chance {
  id: string;
  betrieb_id: string;
  produkt: Produkt;
  phase: ChancePhase;
  score: number;
  begruendung: string | null;
  anfrage_id: string | null;
  naechster_kontakt: string | null;
  letzter_kontakt_at: string | null;
  notizen: string | null;
  quelle: string | null;
  created_at: string;
  updated_at: string;
}

/** Inhalt & Aktualität der Website (`web_technik.inhalt`, seit 09.09.2026). */
export interface WebInhalt {
  aktuelles: boolean;
  aktuelles_url: string | null;
  juengstes_datum: string | null;
  alter_tage: number | null;
  cta_oben: boolean | null;
  ort_im_titel: boolean | null;
  ort_in_h1: boolean | null;
  leistungsseiten: number | null;
  h2_mit_leistung: boolean | null;
  wortzahl_start: number | null;
  faq_schema: boolean;
  service_schema: boolean;
  bewertungen: boolean;
  ansprache: { zielgruppe: boolean; nutzen: boolean; aufforderung: boolean; floskeln: boolean; hinweis: string } | null;
}

/** Technik-Befund aus dem Crawl (Modul A, `web_technik`). */
export interface WebTechnik {
  generator: string | null;
  viewport: boolean | null;
  https: boolean | null;
  copyright_jahr: number | null;
  formular: boolean | null;
  tel_link: boolean | null;
  schema: boolean | null;
  standortseiten: number | null;
  inhalt?: WebInhalt | null;
  /** Ladezeit der Startseite (seit 09.09.2026). */
  speed?: {
    quelle: 'pagespeed' | 'eigen';
    score: number | null;
    lcp_s: number | null;
    ttfb_ms: number | null;
    ladezeit_s: number | null;
    stufe: 'gut' | 'mittel' | 'kritisch';
    gemessen_at: string;
  } | null;
}

/** Google-Ads-Signal je Gewerk+Ort (`ads_befunde`). */
export interface AdsBefunde {
  begriff: string;
  /** Vom Suchaktor gesehene Werbende — praktisch immer leer, KEINE Messung. */
  werbende: string[];
  betrieb_wirbt: boolean;
  nachfrage_monat: number | null;
  /** Anzeigen-Wettbewerb laut Keyword Planner (seit 09.09.2026). */
  wettbewerb?: 'LOW' | 'MEDIUM' | 'HIGH' | null;
  wettbewerb_index?: number | null;
  wettbewerb_begriff?: string | null;
  cpc_min_eur?: number | null;
  cpc_max_eur?: number | null;
  hinweis?: string | null;
}

/** Textbausteine der Analyse (Opus, `analyse-text.ts`). Leerer String = Abschnitt weglassen. */
export interface AnalyseText {
  ueberschrift: string;
  einleitung: string;
  social: string;
  website: string;
  google: string;
  recruiting: string;
  naechste_schritte: string[];
  drei_befunde: string[];
}

export interface Betrieb extends LeadpoolLead {
  strasse: string | null;
  /**
   * Aus der Postleitzahl abgeleitet (Server: plz-bundesland.ts), von Hand
   * korrigierbar. Entscheidet, welche Feiertage bei den Anruffristen zählen.
   */
  bundesland: Bundesland | null;
  anrede: Anrede | null;
  ansprechpartner: string | null;
  web_score: number | null;
  web_befunde: string[] | null;
  web_technik: WebTechnik | null;
  recruiting: boolean | null;
  recruiting_hinweis: string | null;
  ads_score: number | null;
  ads_befunde: AdsBefunde | null;
  publivo_org_id: string | null;
  publivo_status: PublivoStatus | null;
  publivo_trial_bis: string | null;
  publivo_beitraege: number | null;
  publivo_letzter_login: string | null;
  publivo_sync_at: string | null;
  customer_id: string | null;
  analyse_text: AnalyseText | null;
  analyse_text_at: string | null;
  anschreiben_befunde: string[] | null;
  brief_erstellt_at: string | null;
  /** In der Liste immer dabei; im Detail liegen die Chancen separat. */
  chancen: Chance[];
  /** Stand im Briefablauf — nur in der Liste, vom Server berechnet. */
  sales?: SalesStand;
}

export type KontaktArt = 'anruf' | 'brief' | 'email' | 'demo' | 'phase' | 'notiz' | 'publivo';
/**
 * Fünf Ergebnisse (Nico, 12.09.2026). Vorher acht — drei davon endeten am
 * Telefon in derselben Handlung: sprechen und einen neuen Tag ausmachen. Das
 * ist jetzt `spaeter` mit Pflicht-Datum. `erreicht` und `rueckruf` bleiben als
 * ALTE Werte im Protokoll lesbar, werden aber nicht mehr angeboten.
 */
export type AnrufErgebnis = 'nicht_erreicht' | 'mailbox' | 'spaeter' | 'kein_interesse' | 'termin';
export type AnrufErgebnisAlt = 'erreicht' | 'rueckruf';
/** Eine Zeile des Kontaktprotokolls (Tabelle kontakte, 07.09.2026). */
export interface Kontakt {
  id: string;
  betrieb_id: string;
  produkt: Produkt | null;
  art: KontaktArt;
  /** bei anruf ein AnrufErgebnis, bei phase die neue Phase */
  ergebnis: string | null;
  notiz: string | null;
  termin_am: string | null;
  user_id: string | null;
  created_at: string;
}

export interface BetriebDetail {
  betrieb: Betrieb;
  chancen: Chance[];
  anfragen: CrmAnfrage[];
  /** Kontaktprotokoll, neueste zuerst (hoechstens 100). */
  kontakte: Kontakt[];
  /**
   * Brief-Scans: QR-Code-Aufrufe mit utm_content=<domain>. `ziel` sagt, welcher
   * QR-Code gescannt wurde (nico-els.de im alten Anschreiben, publivo.app/de/demo
   * auf der Seite „Publivo erklaert", seit 28.09.2026 formularfrei.ai auf der
   * Seite „formularfrei erklaert"), `brief` welche Briefstufe ihn getragen hat —
   * null bei Scans von vor dem 12.09.2026.
   */
  scans: Array<{ at: string; path: string; brief: 1 | 2 | null; ziel: 'nico-els' | 'publivo' | 'formularfrei'; art: 'scan' | 'termin' }>;
  publivo: {
    status: PublivoStatus | null;
    trial_bis: string | null;
    beitraege: number | null;
    letzter_login: string | null;
    sync_at: string | null;
  };
}

/** Stand im Vertriebsablauf (Server: api/_lib/sales-stufe.ts). */
export type SalesStufe =
  | 'brief_1' | 'anruf_1' | 'brief_2' | 'anruf_2' | 'anruf_3' | 'archiviert' | 'laeuft' | 'aus';
export interface SalesStand {
  stufe: SalesStufe;
  /** ISO-Datum, ab dem der Schritt dran ist; null = sofort. */
  faellig_am: string | null;
  faellig: boolean;
  /** Klartext für die Arbeitsliste: warum steht das an. */
  grund: string;
  /** Laufende Anrufrunde (0 = keine). */
  runde: 0 | 1 | 2 | 3;
  versuche: number;
  versuche_soll: number;
  brief_1_am: string | null;
  /** Geschätzter Zustelltag (Erzeugung + Puffer für Druck und Post). */
  brief_1_zugestellt_am?: string | null;
  brief_2_zugestellt_am?: string | null;
  brief_2_am: string | null;
  /** Jüngster QR-Scan nach dem Erstbrief. */
  scan_am: string | null;
  /** Braucht Brief 2 die Fassung „telefonisch nicht erreicht"? */
  nicht_erreicht_fassung: boolean;
  /** Wählversuch ohne Ergebnis — soll nachgetragen werden. */
  offener_anruf: { id: string; seit: string } | null;
}

export const SALES_STUFE_LABEL: Record<SalesStufe, string> = {
  brief_1: 'Erstbrief',
  anruf_1: 'Anruf nach Scan',
  brief_2: 'Nachfassbrief',
  anruf_2: 'Angekündigter Anruf',
  anruf_3: 'Letzter Anlauf',
  archiviert: 'Archiviert',
  laeuft: 'im Gespräch',
  aus: '—',
};

export interface BetriebeFilter {
  status?: string;
  gruppe?: string;
  gewerk?: string;
  produkt?: string;
  phase?: string;
  q?: string;
  /** Nur Betriebe, deren bester Chance-Score mindestens so hoch ist (Filter „nur Score ab 60"). */
  min_score?: number;
  /** Nur was heute dran ist. 'anruf' fasst alle drei Anrufrunden zusammen. */
  faellig?: SalesStufe | 'anruf';
}

export async function getBetriebe(filter: BetriebeFilter = {}): Promise<Betrieb[]> {
  const p = new URLSearchParams();
  for (const k of ['status', 'gruppe', 'gewerk', 'produkt', 'phase', 'q', 'faellig'] as const) {
    if (filter[k]) p.set(k, String(filter[k]));
  }
  if (filter.min_score) p.set('min_score', String(filter.min_score));
  const res = await analyticsFetch(`/api/crm/betriebe${p.toString() ? `?${p}` : ''}`);
  const json = await parseJson<{ betriebe: Betrieb[] }>(res);
  // chancen immer als Array — die Seite rechnet damit ohne Sonderfall
  return (json.betriebe || []).map((b) => ({ ...b, chancen: Array.isArray(b.chancen) ? b.chancen : [] }));
}

export async function getBetrieb(id: string): Promise<BetriebDetail> {
  const res = await analyticsFetch(`/api/crm/betriebe?id=${encodeURIComponent(id)}`);
  const json = await parseJson<BetriebDetail>(res);
  return {
    ...json,
    chancen: Array.isArray(json.chancen) ? json.chancen : [],
    anfragen: Array.isArray(json.anfragen) ? json.anfragen : [],
    scans: Array.isArray(json.scans) ? json.scans : [],
    betrieb: { ...json.betrieb, chancen: Array.isArray(json.chancen) ? json.chancen : [] },
  };
}

async function betriebePost<T>(body: Record<string, unknown>): Promise<T> {
  const res = await authedFetch('/api/crm/betriebe', { method: 'POST', body: JSON.stringify(body) });
  return parseJson<T>(res);
}

/** Ergebnis eines Impressum-Laufs — je Betrieb, was ergänzt wurde. */
export interface ImpressumErgebnis {
  gelaufen: number;
  offen: number;
  ergaenzt: Array<{ id: string; firma: string; felder: string[]; quelle: string | null }>;
  leer: Array<{ id: string; firma: string; grund?: string }>;
}

/**
 * Anschrift, Telefon, E-Mail und Ansprechpartner aus dem Impressum nachtragen —
 * die Voraussetzung für den Brief. Ohne `ids` nimmt der Server die nächsten 25
 * Betriebe ohne Straße.
 */
export async function impressumNachtragen(ids?: string[]): Promise<ImpressumErgebnis> {
  return betriebePost<ImpressumErgebnis>({ action: 'impressum', ...(ids?.length ? { ids } : {}) });
}

/**
 * Betrieb endgültig löschen — mit ihm Chancen und Kontaktprotokoll.
 * Eingegangene Anfragen bleiben erhalten. Die Domain wird gemerkt, damit
 * Publivo-Abgleich, Import und Leadlisten-Lauf sie nicht wieder anlegen.
 */
export async function betriebLoeschen(
  id: string,
  opts: { grund?: string; wieder_erlauben?: boolean } = {},
): Promise<{ ok: boolean; firma: string; chancen: number; kontakte: number; ignoriert: boolean }> {
  return betriebePost({ action: 'loeschen', id, ...opts })
}

export interface BetriebPatch {
  status?: BetriebStatus;
  notizen?: string;
  anrede?: Anrede | null;
  ansprechpartner?: string | null;
  strasse?: string | null;
  plz?: string | null;
  ort?: string | null;
  telefon?: string | null;
  email?: string | null;
  /**
   * Instagram-Handle von Hand (Nico, 06.09.2026): setzt/korrigiert die
   * Zuordnung der Google-Suche. Der Server normalisiert (@, URL, Leerzeichen)
   * und leert bei Wechsel die alten Messwerte; null/'' löscht das Handle, die
   * nächste Prüfung sucht dann wieder per Google.
   */
  instagram_handle?: string | null;
}

/** Arbeitsstand und Kontaktdaten des Betriebs. Status „angerufen" zieht alle Kandidaten-Chancen mit. */
export function updateBetrieb(id: string, patch: BetriebPatch): Promise<{ betrieb: Betrieb }> {
  return betriebePost({ action: 'status', id, ...patch });
}

export interface ChancePatch {
  phase?: ChancePhase;
  notizen?: string;
  naechster_kontakt?: string | null;
  score?: number;
}

/** Upsert einer Chance (Betrieb × Produkt). */
export function setChance(id: string, produkt: Produkt, patch: ChancePatch): Promise<{ chance: Chance }> {
  return betriebePost({ action: 'chance', id, produkt, ...patch });
}

/** Legt eine CRM-Anfrage (quelle „Betriebe") an und hängt sie an die Chance; Phase → gespraech. */
export function anfrageAusBetrieb(id: string, produkt: Produkt): Promise<{ anfrage: CrmAnfrage; chance: Chance }> {
  return betriebePost({ action: 'anfrage', id, produkt });
}

export function importBetriebe(urls: string[], quelle?: string, quelle_detail?: string): Promise<{ angelegt: number; uebersprungen: number }> {
  return betriebePost({ action: 'import', urls, quelle, quelle_detail });
}

export function entdeckeBetriebe(params: { gewerk: string; ort?: string; orte?: string[]; bundesweit?: boolean; max?: number; pro_ort?: number }): Promise<{ gefunden: number; angelegt: number; ids: string[]; orte: string[] }> {
  return betriebePost({ action: 'entdecken', ...params });
}

/** Crawl + Social + Website/Recruiting/Ads-Signale, legt Chancen an (~1–3 min). `fehler` gesetzt, wenn die Prüfung scheiterte. */
export function pruefeBetrieb(id: string): Promise<{ betrieb?: Betrieb; fehler?: string }> {
  return betriebePost({ action: 'pruefen', id });
}

/** Publivo-Konten (Testphase/Kunde) auf Betriebe abgleichen — dieselbe Funktion wie der Nacht-Cron. */
export function syncPublivo(): Promise<{ abgeglichen: number; treffer: number }> {
  return betriebePost({ action: 'sync_publivo' });
}

/**
 * Anruf, E-Mail oder Notiz protokollieren (POST action 'kontakt'). Nebenwirkungen
 * regelt der Server: spaeter -> Chance auf „angerufen" + Wiedervorlage,
 * termin -> „gespraech" + nächster Kontakt, kein_interesse -> Status „nein" +
 * offene Chancen verloren. nicht_erreicht und mailbox werden nur protokolliert;
 * wann der nächste Versuch dran ist, rechnet der Ablauf aus den Regeln.
 * Antwort: der Kontakt und der Betrieb mit Chancen.
 */
export function protokolliereKontakt(
  id: string,
  patch: {
    art: 'anruf' | 'email' | 'notiz'; ergebnis?: AnrufErgebnis | null; notiz?: string | null;
    produkt?: Produkt | null; termin_am?: string | null;
    /** Ergänzt den Wählversuch aus `anrufBegonnen` statt eine zweite Zeile anzulegen. */
    kontakt_id?: string | null;
  },
): Promise<{ kontakt: Kontakt; betrieb: Betrieb }> {
  return betriebePost({ action: 'kontakt', id, ...patch });
}

/**
 * „Hat sich gemeldet": stoppt die Briefserie, ohne eine Phase zu setzen.
 * Für den Fall, dass jemand auf den ersten Brief zurückschreibt oder anruft —
 * der Nachfassbrief soll dann nicht so tun, als hätte man nichts gehört.
 */
export function hatSichGemeldet(id: string, notiz?: string): Promise<{ kontakt: Kontakt; betrieb: Betrieb }> {
  return betriebePost({ action: 'gemeldet', id, notiz: notiz ?? null });
}

/**
 * Klick auf die Telefonnummer: protokolliert sofort einen Anruf OHNE Ergebnis,
 * damit kein Versuch verloren geht (Nico, 07.09.2026). Das Ergebnis trägt der
 * Anruf-Dialog nach; bleibt es aus, steht die Zeile als offener Anruf da.
 * Ein zweiter Klick innerhalb von 30 Minuten nutzt denselben Eintrag.
 */
export function anrufBegonnen(id: string, produkt?: Produkt | null): Promise<{ kontakt: Kontakt; wiederverwendet: boolean }> {
  return betriebePost({ action: 'anruf_start', id, produkt: produkt ?? null });
}

// ── Vertriebs-Cockpit (07.09.2026) ────────────────────────────────────────
export interface CockpitWoche {
  /** ISO-Woche, z. B. 2026-W37 */
  woche: string;
  /** Montag der Woche, YYYY-MM-DD */
  start: string;
  briefe: number;
  anrufe: number;
  erreicht: number;
  demos: number;
  testphasen: number;
  kunden: number;
  scans: number;
}
export type CockpitSumme = Omit<CockpitWoche, 'woche' | 'start'>;
export interface CockpitDaten {
  zeitraum: { von: string; bis: string };
  wochen: CockpitWoche[];
  summe: CockpitSumme;
  /** Gleich langer Zeitraum davor, fuer die Veraenderung; null ohne Daten. */
  vergleich: CockpitSumme | null;
  /** Anteile 0..1 bzw. Faktor; null, wenn der Nenner 0 ist. */
  quoten: {
    erreicht_je_anruf: number | null;
    demo_je_erreicht: number | null;
    test_je_demo: number | null;
    kunde_je_test: number | null;
    anrufe_je_kunde: number | null;
    briefe_je_demo: number | null;
  };
  /**
   * Aktueller Bestand der CHANCEN je Phase (Produktfilter beachtet). Ein
   * Betrieb kann für bis zu fünf Produkte gleichzeitig Kandidat sein — ohne
   * Produktfilter ist diese Zahl also größer als die Zahl der Betriebe.
   */
  pipeline: Record<ChancePhase, number>;
  /** Wie viele BETRIEBE hinter den Chancen je Phase stehen. */
  pipeline_betriebe?: Record<ChancePhase, number>;
  gewerke: Array<{ gewerk: string; label: string; betriebe: number; angerufen: number; demos: number; kunden: number }>;
  /** Anstehende Termine und Rueckrufe der naechsten 14 Tage. */
  termine: Array<{ betrieb_id: string; firma: string; produkt: Produkt | null; termin_am: string; art: string; ergebnis: string | null }>;
  heute: { anrufe: number; erreicht: number; demos: number };
  /** Die letzten 20 Protokolleintraege mit Firma. */
  letzte: Array<{ id: string; betrieb_id: string; firma: string; art: KontaktArt; ergebnis: string | null; produkt: Produkt | null; created_at: string }>;
}
export async function getCockpit(params: { von?: string; bis?: string; produkt?: Produkt | '' } = {}): Promise<CockpitDaten> {
  const p = new URLSearchParams();
  if (params.von) p.set('von', params.von);
  if (params.bis) p.set('bis', params.bis);
  if (params.produkt) p.set('produkt', params.produkt);
  const res = await analyticsFetch(`/api/crm/cockpit${p.toString() ? `?${p}` : ''}`);
  return parseJson<CockpitDaten>(res);
}

// Analyse-PDF und Anschreiben (/api/crm/analyse-pdf)

export function analysePdfUrl(id: string, mitAnschreiben: boolean, stufe: 1 | 2 = 1): string {
  return `/api/crm/analyse-pdf?id=${encodeURIComponent(id)}&mit_anschreiben=${mitAnschreiben ? 1 : 0}&stufe=${stufe}`;
}

/**
 * Ein GET mit Bearer-Header lässt sich nicht als neuer Tab öffnen. Deshalb:
 * Tab SOFORT im Klick öffnen (sonst greift der Popup-Blocker), PDF per fetch
 * mit Authorization holen und die Blob-URL in den wartenden Tab schieben.
 * Der Tab muss vor jedem `await` aufgehen — darum ist das Öffnen ein eigener
 * Schritt, den die Seite ganz am Anfang ihres Klick-Handlers aufruft.
 */
export function pdfFensterOeffnen(hinweis = 'PDF wird erzeugt … das dauert bei neuem Text bis zu einer Minute.'): Window | null {
  const fenster = window.open('', '_blank');
  if (fenster) {
    try {
      fenster.document.write(`<title>PDF wird erzeugt</title><p style="font-family:system-ui,sans-serif;padding:2rem;color:#374151">${hinweis}</p>`);
    } catch { /* fremde Origin o. ä. — dann bleibt der Tab leer */ }
  }
  return fenster;
}

export async function openAnalysePdf(id: string, mitAnschreiben: boolean, fenster?: Window | null, stufe: 1 | 2 = 1): Promise<void> {
  const ziel = fenster === undefined ? pdfFensterOeffnen() : fenster;
  try {
    const res = await analyticsFetch(analysePdfUrl(id, mitAnschreiben, stufe));
    const { url, dateiname } = await pdfHolen(res, mitAnschreiben ? (stufe === 2 ? 'Nachfassbrief.pdf' : 'Anschreiben.pdf') : 'Analyse.pdf');
    if (ziel && !ziel.closed) {
      ziel.location.href = url;
      // Erst freigeben, wenn der Viewer geladen hat — sonst zeigt der Tab nichts.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } else {
      pdfSpeichern(url, dateiname);
    }
  } catch (e) {
    try { ziel?.close(); } catch { /* egal */ }
    throw e;
  }
}

async function analysePost<T>(body: Record<string, unknown>): Promise<T> {
  const res = await authedFetch('/api/crm/analyse-pdf', { method: 'POST', body: JSON.stringify(body) });
  return parseJson<T>(res);
}

/** Textbausteine + drei Befunde erzeugen bzw. aus dem Cache holen (Vorschau vor dem Brief). */
export function analyseText(id: string): Promise<{ text: AnalyseText }> {
  return analysePost({ id, action: 'text' });
}

/** Vorschau des Briefs (seit 09.09.2026): Betreff und die drei Fakten aus den Messwerten, ohne Opus. */
export function befundeVorschlag(id: string): Promise<{ befunde: string[]; betreff: string | null; muster: string | null; quelle: 'analyse' | 'messwerte'; moeglich?: { ok: boolean; grund?: string } }> {
  return analysePost({ id, action: 'vorschlag' });
}

/** Die drei Befunde fürs Anschreiben speichern (`anschreiben_befunde`). */
export function setzeBefunde(id: string, befunde: string[]): Promise<{ ok?: boolean; betrieb?: Betrieb }> {
  return analysePost({ id, action: 'befunde', befunde: befunde.slice(0, 3) });
}

/** Merkt sich, dass der Brief erzeugt wurde (`brief_erstellt_at`). */
export function briefErstellt(id: string, stufe: 1 | 2 = 1): Promise<{ ok?: boolean; betrieb?: Betrieb }> {
  return analysePost({ id, action: 'brief_erstellt', stufe });
}

/** Wer wäre beim Serienbrief dieser Stufe dran? (Vorschau, ohne PDF) */
export async function serienbriefVorschau(stufe: 1 | 2, anzahl: number | 'alle'): Promise<{
  stufe: 1 | 2;
  anzahl: number;
  /**
   * `frisch`: seit dem Erstbrief noch einmal geprüft — Voraussetzung für die
   * Nachschau im Nachfassbrief. `nicht_erreicht`: so oft haben wir seit dem
   * QR-Scan erfolglos angerufen; ab 1 bekommt der Brief die zweite Fassung.
   */
  betriebe: Array<{ id: string; firma: string; ort: string | null; hat_text: boolean; frisch?: boolean; nicht_erreicht?: number }>;
  gesperrt?: Array<{ firma: string; grund: string }>;
  /** Nur bei Stufe 1: Wochenziel für Erstbriefe und was davon schon raus ist. */
  wochenziel?: { ziel: number; schon: number; rest: number } | null;
}> {
  const res = await analyticsFetch(`/api/crm/serienbrief?stufe=${stufe}&anzahl=${anzahl}`);
  return parseJson(res);
}

/**
 * Serienbrief bauen: ein PDF mit allen Briefen, danach sind die Betriebe als
 * angeschrieben protokolliert. Öffnet den wartenden Tab mit dem Ergebnis.
 */
export async function serienbriefErzeugen(
  stufe: 1 | 2,
  anzahl: number | 'alle',
  fenster?: Window | null,
): Promise<{ briefe: number; seiten: number; uebersprungen: number }> {
  const ziel = fenster === undefined ? pdfFensterOeffnen('Serienbrief wird gebaut … bei 50 Briefen dauert das eine Weile.') : fenster;
  try {
    const res = await authedFetch('/api/crm/serienbrief', {
      method: 'POST',
      body: JSON.stringify({ stufe, anzahl }),
    });
    const briefe = Number(res.headers.get('X-Serienbrief-Briefe') || 0);
    const seiten = Number(res.headers.get('X-Serienbrief-Seiten') || 0);
    const uebersprungen = Number(res.headers.get('X-Serienbrief-Uebersprungen') || 0);
    const { url, dateiname } = await pdfHolen(res, 'Serienbrief.pdf');
    if (ziel && !ziel.closed) {
      ziel.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 120000);
    } else {
      pdfSpeichern(url, dateiname);
    }
    return { briefe, seiten, uebersprungen };
  } catch (e) {
    try { ziel?.close(); } catch { /* egal */ }
    throw e;
  }
}

// ── Vertriebsablauf (12.09.2026) ──────────────────────────────────────────
// Vertrag zu api/crm/vertrieb.ts. Die Regeln leben in der Datenbank, damit
// dieselben Zahlen rechnen, die auf der Ablaufseite stehen — ein gemaltes
// Schaubild wäre nach der ersten Änderung falsch.

export type Bundesland =
  | 'BW' | 'BY' | 'BE' | 'BB' | 'HB' | 'HH' | 'HE' | 'MV'
  | 'NI' | 'NW' | 'RP' | 'SL' | 'SN' | 'ST' | 'SH' | 'TH';

export const BUNDESLAND_NAME: Record<Bundesland, string> = {
  BW: 'Baden-Württemberg', BY: 'Bayern', BE: 'Berlin', BB: 'Brandenburg',
  HB: 'Bremen', HH: 'Hamburg', HE: 'Hessen', MV: 'Mecklenburg-Vorpommern',
  NI: 'Niedersachsen', NW: 'Nordrhein-Westfalen', RP: 'Rheinland-Pfalz',
  SL: 'Saarland', SN: 'Sachsen', ST: 'Sachsen-Anhalt', SH: 'Schleswig-Holstein',
  TH: 'Thüringen',
};

export interface SalesRegeln {
  erstbriefe_pro_woche: number;
  leadtag: number;
  brieftag: number;
  zustell_puffer_tage: number;
  tage_bis_nachfass: number;
  werktage_bis_anruf: number;
  runde1_versuche: number;
  runde1_abstand_tage: number;
  runde2_versuche: number;
  runde2_abstand_tage: number;
  spaeter_vorbelegung_tage: number;
  ruhe_monate: number;
  nachruhe_versuche: number;
  nachschau_vorlauf_tage: number;
}
export type SalesRegelFeld = keyof SalesRegeln;
export interface SalesRegelGrenze { min: number; max: number; label: string; hinweis: string }
export interface SalesPause { id: string; von: string; bis: string; grund: string | null }

/** Eine Zeile im Arbeitsstapel — genug für Anrufen und Anklicken, nicht mehr. */
export interface VertriebZeile {
  id: string;
  firma: string;
  ort: string | null;
  telefon: string | null;
  bundesland: Bundesland | null;
  score: number;
  sales: SalesStand;
}

export type VertriebStapelName =
  | 'offene_anrufe' | 'nach_scan' | 'anrufe' | 'brief_1' | 'brief_2' | 'termine' | 'archiv_reif';

export interface VertriebWoche {
  heute: string;
  regeln: SalesRegeln;
  /** Läuft gerade eine eingetragene Pause (Urlaub, Messe)? */
  pause: { von: string; bis: string } | null;
  /** Feiertage heute, je Bundesland, in dem Betriebe liegen. */
  feiertage: Array<{ land: string; name: string | null }>;
  wochenziel: { ziel: number; schon: number; rest: number; vorrat: number };
  stapel: Record<VertriebStapelName, VertriebZeile[]>;
  anzahl: Record<VertriebStapelName, number>;
}

export async function getVertriebWoche(): Promise<VertriebWoche> {
  const res = await analyticsFetch('/api/crm/vertrieb?view=woche');
  return parseJson<VertriebWoche>(res);
}

export interface VertriebRegelnAntwort {
  regeln: SalesRegeln;
  grenzen: Record<SalesRegelFeld, SalesRegelGrenze>;
  standard: SalesRegeln;
  pausen: SalesPause[];
}

export async function getVertriebRegeln(): Promise<VertriebRegelnAntwort> {
  const res = await analyticsFetch('/api/crm/vertrieb?view=regeln');
  return parseJson<VertriebRegelnAntwort>(res);
}

/** Eine Kette je Betrieb: was aus dem Brief geworden ist. */
export interface KettenZeile {
  id: string;
  firma: string;
  ort: string | null;
  brief_1: string | null;
  brief_2: string | null;
  scan: string | null;
  scan_brief: 1 | 2 | null;
  /** Termin direkt aus dem QR-Code gebucht, ohne Anruf. */
  buchung: boolean;
  anrufe: number;
  termin: string | null;
  absage: string | null;
  testphase: string | null;
  kunde: string | null;
  publivo_status: PublivoStatus | null;
}

export interface VertriebKette {
  zeilen: KettenZeile[];
  /** Zählt NUR Betriebe, die einen Erstbrief bekommen haben. */
  trichter: Array<{ stufe: string; anzahl: number }>;
  /** Publivo-Konten, die es ohne Brief gibt — nicht im Trichter, aber im Bild. */
  ohne_brief: { kunden: number; testphasen: number };
}

export async function getVertriebKette(): Promise<VertriebKette> {
  const res = await analyticsFetch('/api/crm/vertrieb?view=kette');
  return parseJson<VertriebKette>(res);
}

async function vertriebPost<T>(body: Record<string, unknown>): Promise<T> {
  const res = await authedFetch('/api/crm/vertrieb', { method: 'POST', body: JSON.stringify(body) });
  return parseJson<T>(res);
}

export function speichereRegeln(werte: Partial<SalesRegeln>): Promise<{ regeln: SalesRegeln }> {
  return vertriebPost({ action: 'regeln', werte });
}

export function legePauseAn(von: string, bis: string, grund?: string): Promise<{ pausen: SalesPause[] }> {
  return vertriebPost({ action: 'pause_neu', von, bis, grund: grund ?? null });
}

export function loeschePause(id: string): Promise<{ pausen: SalesPause[] }> {
  return vertriebPost({ action: 'pause_weg', id });
}

// ── Live-Ansicht (12.09.2026) ─────────────────────────────────────────────
// Vertrag zu api/analytics/live.ts. Quelle ist das eigene Tracking (t.js),
// nicht Google Analytics — die Ereignisse liegen ohnehin in der Datenbank.

/** Wo ein Besucher GERADE steht — aus dem Lebenszeichen, nicht aus dem letzten Ereignis. */
export interface LiveJetzt {
  pfad: string | null;
  abschnitt: string | null;
  /** Sekunden im aktuellen Abschnitt. */
  abschnitt_s: number;
  /** Sekunden auf der aktuellen Seite. */
  pfad_s: number;
  /** Sekunden ohne jede Bewegung, gemessen bis zum letzten Lebenszeichen. */
  bewegt_s: number;
  scroll: number | null;
  formular: boolean;
  zuletzt: string;
}

export interface LiveBesucher {
  /** „<slug>:<hash>:<beginn in ms>" — der Beginn trennt zwei Besuche desselben Tages. */
  id: string;
  site: string;
  site_name: string;
  /** Erstes und letztes Ereignis im Fenster. */
  seit: string;
  zuletzt: string;
  /**
   * Wann sich zuletzt wirklich etwas bewegt hat — Seite, Abschnitt,
   * Scrolltiefe oder ein Ereignis. Nicht dasselbe wie `zuletzt`: Ein offener
   * Tab meldet sich jede Minute, ohne dass jemand etwas tut. Danach sortiert
   * die Liste.
   */
  bewegt: string;
  /** Ereignis in den letzten fünf Minuten. */
  online: boolean;
  pfad: string | null;
  /** Der Weg durch die Seite, ältester Aufruf zuerst. */
  weg: string[];
  seiten: number;
  kanal: string | null;
  quelle: string | null;
  kampagne: string | null;
  /** Gebuchtes Keyword aus dem Anzeigenklick — nicht der getippte Suchbegriff. */
  keyword: string | null;
  device: string | null;
  land: string | null;
  lesezeit_s: number;
  scroll_max: number | null;
  /** tel, mail, outbound, cta:… */
  aktionen: string[];
  /** Wurde aus dieser Sitzung eine Anfrage? Exakter Treffer über den Sitzungsschlüssel. */
  anfrage: { id: string; name: string | null; am: string } | null;
  /**
   * Lebenszeichen — wo der Besucher GERADE ist. Nur gesetzt, solange er
   * wirklich da ist und seine Seite die neue Fassung des Trackers geladen hat.
   */
  jetzt: LiveJetzt | null;
}

export interface LiveEreignis {
  at: string;
  site: string;
  site_name: string;
  pfad: string | null;
  art: 'pageview' | 'click' | 'engagement';
  name: string | null;
  dauer_s: number | null;
  scroll: number | null;
  besucher: string | null;
  kanal: string | null;
  quelle: string | null;
}

export interface LiveSitzung {
  id: string;
  site: string;
  site_name: string;
  seit: string;
  zuletzt: string;
  online: boolean;
  kanal: string | null;
  quelle: string | null;
  kampagne: string | null;
  keyword: string | null;
  device: string | null;
  land: string | null;
  einstieg: string | null;
  /** Wie oft dieser Schlüssel heute überhaupt da war, und der wievielte Besuch hier offen ist. */
  besuche: number;
  besuch_nr: number;
  /** Der Weg mit Zeiten. `lesezeit_s` ist null, solange die Seite noch offen ist. */
  weg: Array<{ pfad: string | null; at: string; lesezeit_s: number | null; scroll: number | null; aktionen: string[] }>;
  aktionen: Array<{ name: string; at: string; pfad: string | null }>;
  /**
   * Der Herzschlag in historischer Reihenfolge: ein Eintrag je Wechsel von
   * Seite oder Abschnitt. Nur hier, nicht in der Liste — die zeigt Sitzungen.
   */
  puls: Array<{ at: string; pfad: string | null; abschnitt: string | null; scroll: number | null; formular: boolean }>;
  summe: { seiten: number; lesezeit_s: number; scroll_max: number | null; kontaktklicks: number };
  anfrage: { id: string; name: string | null; am: string } | null;
  /** Wo der Besucher gerade steht — nur solange er wirklich da ist. */
  jetzt: LiveJetzt | null;
}

export async function getLiveSitzung(id: string): Promise<LiveSitzung> {
  const res = await analyticsFetch(`/api/analytics/live?sitzung=${encodeURIComponent(id)}`);
  return parseJson<LiveSitzung>(res);
}

export interface LiveDaten {
  jetzt: string;
  /** Seitenaufrufe je Minute über das Fenster, ältester Eimer zuerst. */
  takt: number[];
  kontaktklicks: number;
  fenster_minuten: number;
  online_minuten: number;
  online: number;
  aktiv: number;
  seitenaufrufe: number;
  /** Das Fenster lieferte mehr Ereignisse, als geladen wurden. */
  gekappt: boolean;
  sites: Array<{ slug: string; name: string; online: number; aktiv: number }>;
  besucher: LiveBesucher[];
  strom: LiveEreignis[];
}

export async function getLive(minuten = 30, site = ''): Promise<LiveDaten> {
  const p = new URLSearchParams({ minuten: String(minuten) });
  if (site) p.set('site', site);
  const res = await analyticsFetch(`/api/analytics/live?${p}`);
  return parseJson<LiveDaten>(res);
}

// ── Keyword im Tracking (12.09.2026) ──────────────────────────────────────
// Vertrag zu api/analytics/ads-tracking.ts.

export interface AdsTrackingProjekt {
  project_id: string;
  projekt: string;
  kunde: string | null;
  ads_account_id: string;
  konto_suffix: string | null;
  /** Was nach dem Setzen im Konto stünde. */
  konto_neu: string;
  kampagnen_mit_eigenem: Array<{ id: string; name: string; suffix: string; neu: string }>;
  hat_keyword: boolean;
  fehler?: string;
}

export async function getAdsTracking(): Promise<{ projekte: AdsTrackingProjekt[]; offen: number }> {
  const res = await analyticsFetch('/api/analytics/ads-tracking');
  return parseJson(res);
}

export async function setzeAdsTracking(projectId?: string): Promise<{
  gesetzt: Array<{ projekt: string; kunde: string | null; konto: string; kampagnen: number }>;
  fehler: Array<{ projekt: string; grund: string }>;
  offen_danach: number;
}> {
  const res = await authedFetch('/api/analytics/ads-tracking', {
    method: 'POST',
    body: JSON.stringify(projectId ? { project_id: projectId } : {}),
  });
  return parseJson(res);
}

// ── Kanal aufklappen (13.09.2026) ─────────────────────────────────────────
// Vertrag zu api/analytics/kanal.ts: was hinter „Verweise" steckt.

export interface KanalDetail {
  channel: string;
  zeitraum: { von: string; bis: string };
  /** Verweisende Domain bzw. Kampagne. */
  quellen: Array<{ quelle: string; aufrufe: number; besucher: number }>;
  /** Einstiegsseiten — die brauchbare Antwort bei „direkt" und „organisch". */
  seiten: Array<{ path: string; aufrufe: number; besucher: number }>;
}

export async function getKanalDetail(
  channel: string,
  opts: { from?: string; to?: string; projectId?: string | null } = {},
): Promise<KanalDetail> {
  const p = new URLSearchParams({ channel });
  if (opts.from) p.set('from', opts.from);
  if (opts.to) p.set('to', opts.to);
  if (opts.projectId) p.set('project_id', opts.projectId);
  const res = await analyticsFetch(`/api/analytics/kanal?${p}`);
  return parseJson<KanalDetail>(res);
}
